import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react'
import type { Geometry } from 'geojson'
import type {
  SmartFeature,
  SmartLayerType,
  SmartLayerStore,
} from '@/types/draw'

// =============================================================================
// State shape
// =============================================================================

const createInitialLayerState = () => ({
  features: [] as SmartFeature[],
  visible: true,
})

const initialState: SmartLayerStore = {
  points: createInitialLayerState(),
  lines: createInitialLayerState(),
  polygons: createInitialLayerState(),
}

// =============================================================================
// Actions
// =============================================================================

type SmartLayerAction =
  | { type: 'ADD_FEATURE'; payload: SmartFeature }
  | {
      type: 'REMOVE_FEATURE'
      payload: { mamId: string; smartLayer: SmartLayerType }
    }
  /** Remove by mamId only; resolves bucket via {@link findSmartLayerForMamId}. */
  | { type: 'REMOVE_FEATURE_BY_MAM_ID'; payload: { mamId: string } }
  /** Batch remove by mamIds — preferred over N single dispatches. */
  | { type: 'REMOVE_FEATURES_BY_MAM_IDS'; payload: { mamIds: string[] } }
  | { type: 'REMOVE_LAST_FEATURE'; payload: { smartLayer: SmartLayerType } }
  | {
      type: 'UPDATE_FEATURE'
      payload: {
        mamId: string
        geometry: Geometry
        /**
         * Optional. When omitted, only the geometry is patched and existing
         * properties are preserved untouched. Terra Draw's edit handler omits
         * this on vertex updates because Terra's property store roundtrip
         * `String()`-coerces nested values and would clobber the original
         * shape.
         */
        properties?: Record<string, unknown>
      }
    }
  | { type: 'TOGGLE_VISIBILITY'; payload: { smartLayer: SmartLayerType } }
  | { type: 'CLEAR_LAYER'; payload: { smartLayer: SmartLayerType } }
  | { type: 'CLEAR_ALL' }

const SMART_LAYER_KEYS: SmartLayerType[] = ['points', 'lines', 'polygons']

const findSmartLayerForMamId = (
  state: SmartLayerStore,
  mamId: string
): SmartLayerType | null =>
  SMART_LAYER_KEYS.find(k => state[k].features.some(f => f.mamId === mamId)) ??
  null

function smartLayerReducer(
  state: SmartLayerStore,
  action: SmartLayerAction
): SmartLayerStore {
  switch (action.type) {
    case 'ADD_FEATURE': {
      const { smartLayer } = action.payload
      const bucket = state[smartLayer]
      return {
        ...state,
        [smartLayer]: {
          ...bucket,
          features: [...bucket.features, action.payload],
        },
      }
    }
    case 'REMOVE_FEATURE': {
      const { mamId, smartLayer } = action.payload
      return {
        ...state,
        [smartLayer]: {
          ...state[smartLayer],
          features: state[smartLayer].features.filter(f => f.mamId !== mamId),
        },
      }
    }
    case 'REMOVE_FEATURE_BY_MAM_ID': {
      const { mamId } = action.payload
      const smartLayer = findSmartLayerForMamId(state, mamId)
      if (!smartLayer) return state
      return {
        ...state,
        [smartLayer]: {
          ...state[smartLayer],
          features: state[smartLayer].features.filter(f => f.mamId !== mamId),
        },
      }
    }
    case 'REMOVE_FEATURES_BY_MAM_IDS': {
      const { mamIds } = action.payload
      if (mamIds.length === 0) return state
      const idSet = new Set(mamIds)
      return {
        points: {
          ...state.points,
          features: state.points.features.filter(f => !idSet.has(f.mamId)),
        },
        lines: {
          ...state.lines,
          features: state.lines.features.filter(f => !idSet.has(f.mamId)),
        },
        polygons: {
          ...state.polygons,
          features: state.polygons.features.filter(f => !idSet.has(f.mamId)),
        },
      }
    }
    case 'REMOVE_LAST_FEATURE': {
      const { smartLayer } = action.payload
      const features = state[smartLayer].features
      if (features.length === 0) return state
      return {
        ...state,
        [smartLayer]: {
          ...state[smartLayer],
          features: features.slice(0, -1),
        },
      }
    }
    case 'UPDATE_FEATURE': {
      const { mamId, geometry, properties } = action.payload
      const smartLayer = findSmartLayerForMamId(state, mamId)
      if (!smartLayer) return state
      return {
        ...state,
        [smartLayer]: {
          ...state[smartLayer],
          features: state[smartLayer].features.map(f =>
            f.mamId === mamId
              ? {
                  ...f,
                  geometry,
                  properties: properties
                    ? { ...f.properties, ...properties }
                    : f.properties,
                }
              : f
          ),
        },
      }
    }
    case 'TOGGLE_VISIBILITY': {
      const { smartLayer } = action.payload
      return {
        ...state,
        [smartLayer]: {
          ...state[smartLayer],
          visible: !state[smartLayer].visible,
        },
      }
    }
    case 'CLEAR_LAYER': {
      const { smartLayer } = action.payload
      return {
        ...state,
        [smartLayer]: createInitialLayerState(),
      }
    }
    case 'CLEAR_ALL':
      return {
        points: createInitialLayerState(),
        lines: createInitialLayerState(),
        polygons: createInitialLayerState(),
      }
    default:
      return state
  }
}

// =============================================================================
// Context
// =============================================================================

interface SmartLayerContextValue {
  store: SmartLayerStore
  addFeature: (feature: SmartFeature) => void
  removeFeature: (mamId: string, smartLayer: SmartLayerType) => void
  /** Removes a feature from whichever smart bucket contains {@link mamId}. */
  removeFeatureByMamId: (mamId: string) => void
  /** Batch remove — preferred when persisting multiple features at once. */
  removeFeaturesByMamIds: (mamIds: string[]) => void
  /**
   * Patch a feature by mamId. Geometry is always replaced; properties are
   * merged when supplied and left untouched when omitted (callers like the
   * Terra Draw vertex-edit handler omit them on purpose — see
   * {@link useTerraDrawInstance}).
   */
  updateFeatureByMamId: (
    mamId: string,
    geometry: Geometry,
    properties?: Record<string, unknown>
  ) => void
  removeLastFeature: (smartLayer: SmartLayerType) => SmartFeature | null
  toggleVisibility: (smartLayer: SmartLayerType) => void
  clearLayer: (smartLayer: SmartLayerType) => void
  clearAll: () => void
  getFeatureCount: (smartLayer: SmartLayerType) => number
  getFeatures: (smartLayer: SmartLayerType) => SmartFeature[]
  isVisible: (smartLayer: SmartLayerType) => boolean
}

const SmartLayerContext = createContext<SmartLayerContextValue | null>(null)

// =============================================================================
// Provider
// =============================================================================

export const SmartLayerProvider = ({ children }: { children: ReactNode }) => {
  const [store, dispatch] = useReducer(smartLayerReducer, initialState)

  // Mirror of `store` for callbacks that need the latest snapshot but
  // shouldn't recreate on every dispatch (which would churn `useEffect` deps
  // in consumers like `useDrawKeyboardShortcuts`'s document keydown listener).
  const storeRef = useRef(store)
  storeRef.current = store

  const addFeature = useCallback((feature: SmartFeature) => {
    dispatch({ type: 'ADD_FEATURE', payload: feature })
  }, [])

  const removeFeature = useCallback(
    (mamId: string, smartLayer: SmartLayerType) => {
      dispatch({ type: 'REMOVE_FEATURE', payload: { mamId, smartLayer } })
    },
    []
  )

  const removeFeatureByMamId = useCallback((mamId: string) => {
    dispatch({ type: 'REMOVE_FEATURE_BY_MAM_ID', payload: { mamId } })
  }, [])

  const removeFeaturesByMamIds = useCallback((mamIds: string[]) => {
    dispatch({ type: 'REMOVE_FEATURES_BY_MAM_IDS', payload: { mamIds } })
  }, [])

  const updateFeatureByMamId = useCallback(
    (
      mamId: string,
      geometry: Geometry,
      properties?: Record<string, unknown>
    ) => {
      dispatch({
        type: 'UPDATE_FEATURE',
        payload: { mamId, geometry, properties },
      })
    },
    []
  )

  const removeLastFeature = useCallback(
    (smartLayer: SmartLayerType): SmartFeature | null => {
      // Read from the ref so this callback stays stable across store updates.
      const features = storeRef.current[smartLayer].features
      if (features.length === 0) return null
      const last = features[features.length - 1]
      dispatch({ type: 'REMOVE_LAST_FEATURE', payload: { smartLayer } })
      return last
    },
    []
  )

  const toggleVisibility = useCallback((smartLayer: SmartLayerType) => {
    dispatch({ type: 'TOGGLE_VISIBILITY', payload: { smartLayer } })
  }, [])

  const clearLayer = useCallback((smartLayer: SmartLayerType) => {
    dispatch({ type: 'CLEAR_LAYER', payload: { smartLayer } })
  }, [])

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' })
  }, [])

  const getFeatureCount = useCallback(
    (smartLayer: SmartLayerType) => store[smartLayer].features.length,
    [store]
  )

  const getFeatures = useCallback(
    (smartLayer: SmartLayerType) => store[smartLayer].features,
    [store]
  )

  const isVisible = useCallback(
    (smartLayer: SmartLayerType) => store[smartLayer].visible,
    [store]
  )

  const value = useMemo<SmartLayerContextValue>(
    () => ({
      store,
      addFeature,
      removeFeature,
      removeFeatureByMamId,
      removeFeaturesByMamIds,
      updateFeatureByMamId,
      removeLastFeature,
      toggleVisibility,
      clearLayer,
      clearAll,
      getFeatureCount,
      getFeatures,
      isVisible,
    }),
    [
      store,
      addFeature,
      removeFeature,
      removeFeatureByMamId,
      removeFeaturesByMamIds,
      updateFeatureByMamId,
      removeLastFeature,
      toggleVisibility,
      clearLayer,
      clearAll,
      getFeatureCount,
      getFeatures,
      isVisible,
    ]
  )

  return (
    <SmartLayerContext.Provider value={value}>
      {children}
    </SmartLayerContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

export const useSmartLayers = (): SmartLayerContextValue => {
  const context = useContext(SmartLayerContext)
  if (!context) {
    throw new Error('useSmartLayers must be used within a SmartLayerProvider')
  }
  return context
}
